// src/components/signInDropdown/TeamManagement.jsx - Enhanced version
import React, { useState, useEffect } from 'react';
import SigninHeader from '../auth/SigninHeader';
import SideNavigation from './SideNavigation';
import PageHeader from '../shared/PageHeader';
import axios from 'axios';

const TeamManagement = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [user, setUser] = useState(null);
  const [teamData, setTeamData] = useState({
    currentUser: null,
    teamMembers: [],
    statistics: {
      totalMembers: 0,
      activeMembers: 0,
      inactiveMembers: 0,
      deletedMembers: 0,
      departments: 0,
      roles: 0,
      departmentsList: [],
      rolesList: []
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'Member',
    department: 'General'
  });
  const [exportOptions, setExportOptions] = useState({
    format: 'excel',
    filters: {
      status: '',
      department: '',
      userRole: ''
    }
  });

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(savedTheme === 'dark' || (!savedTheme && prefersDark));

    // Load user data
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }

    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please sign in to view team data');
        return;
      }

      const response = await axios.get('http://localhost:5000/api/team/members', {
        headers: { Authorization: token }
      });

      if (response.data.success) {
        setTeamData({ ...response.data.data });
        console.log("Fetched team data:", response.data.data.teamMembers);

      } else {
        setError(response.data.message || 'Failed to load team data');
      }
    } catch (error) {
      console.error('Error fetching team data:', error);
      if (error.response?.status === 401) {
        setError('Your session has expired. Please sign in again.');
      } else if (error.response?.status === 400) {
        setError('You must belong to an agency to view team members');
      } else {
        setError('Failed to load team data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  const getInitials = (member) => {
    return member.initials || 'U';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'suspended':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin':
        return 'bg-purple-100 text-purple-800';
      case 'Manager':
        return 'bg-blue-100 text-blue-800';
      case 'Developer':
        return 'bg-cyan-100 text-cyan-800';
      case 'Analyst':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEditMember = (member) => {
    setEditingMember({
      username: member.username,
      email: member.email,
      firstName: member.firstName,
      lastName: member.lastName,
      mobilePhone: member.mobilePhone,
      department: member.department,
      userRole: member.userRole,
      userStatus: member.userStatus,
      accountStatus: member.accountStatus,
      jurisdictionType: member.jurisdictionType,
      roleAtAgency: member.roleAtAgency,
      emailUpdates: member.emailUpdates,
      areasOfInterest: member.areasOfInterest || []
    });
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `http://localhost:5000/api/team/member/${editingMember.username}`,
        editingMember,
        { headers: { Authorization: token } }
      );

      if (response.data.success) {
        setSuccessMessage('Team member updated successfully');
        setEditingMember(null);
        fetchTeamData(); // Refresh data
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.message || 'Failed to update team member');
      }
    } catch (error) {
      console.error('Error updating team member:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to edit team members');
      } else if (error.response?.status === 409) {
        setError('Email already exists');
      } else {
        setError('Failed to update team member');
      }
    }
  };

  const handleDeleteMember = async (username) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `http://localhost:5000/api/team/member/${username}`,
        { headers: { Authorization: token } }
      );

      if (response.data.success) {
        setSuccessMessage('Team member deleted successfully');
        setShowDeleteModal(null);
        fetchTeamData(); // Refresh data
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.message || 'Failed to delete team member');
      }
    } catch (error) {
      console.error('Error deleting team member:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to delete team members');
      } else {
        setError('Failed to delete team member');
      }
      setShowDeleteModal(null);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        'http://localhost:5000/api/team/export',
        exportOptions,
        { 
          headers: { Authorization: token },
          responseType: 'blob' // Important for file downloads
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename based on format
      const extension = exportOptions.format === 'excel' ? 'xlsx' : 
                       exportOptions.format === 'csv' ? 'csv' : 'pdf';
      link.setAttribute('download', `team_members_${Date.now()}.${extension}`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowExportModal(false);
      setSuccessMessage('Team data exported successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error exporting team data:', error);
      setError('Failed to export team data');
      setShowExportModal(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        'http://localhost:5000/api/team/invite',
        inviteForm,
        { headers: { Authorization: token } }
      );

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setShowInviteModal(false);
        setInviteForm({ email: '', role: 'Member', department: 'General' });
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      setError('Failed to send invitation');
    }
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-main-dark' : 'bg-gray-100'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className={`text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Loading team data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-main-dark text-white' : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Header */}
      <SigninHeader isDarkMode={isDarkMode} isLoggedIn={true} />
      
      {/* Side Navigation */}
      <SideNavigation isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />

      {/* Page Header */}
      <PageHeader 
        title="Team Management" 
        user={user} 
        isDarkMode={isDarkMode} 
        showUserInfo={true} 
      />

      {/* Main Content Container - Fixed Height with Internal Scrolling */}
      <main className="ml-20 pt-[130px] pb-8 px-8 h-screen overflow-hidden">
        <div className="h-full max-w-6xl mx-auto flex flex-col justify-center">
          
          {/* Messages - Outside Scrollable Area */}
          <div className="flex-shrink-0 mb-4">
            {/* Success Message */}
            {successMessage && (
              <div className="mb-4 bg-green-600 text-white p-4 rounded-lg flex items-center gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {successMessage}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg flex items-center gap-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}
          </div>

          {/* Scrollable Team Management Card */}
          <div className="flex-1 overflow-hidden max-h-[calc(100vh-180px)]">
            <div className={`h-full rounded-xl shadow-lg overflow-hidden ${
              isDarkMode ? 'bg-[#1F2023] border-gray-700' : 'bg-white border-gray-200'
            } border flex flex-col`}>
              
              {/* Team Header - Fixed */}
              <div className="flex-shrink-0 bg-gradient-to-r from-[#100E27] to-[#1a1a2e] px-8 py-6 text-center">
                <div className="flex items-center justify-between text-white">
                  <div className="text-left">
                    <h2 className="text-2xl font-bold">{teamData.currentUser?.agencyName || 'Team'}</h2>
                    <p className="text-white/80">Team Management Dashboard</p>
                  </div>
                  <div className="flex gap-4">
                    {teamData.currentUser?.isAdmin && (
                      <>
                        <button
                          onClick={() => setShowInviteModal(true)}
                          className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg transition-colors border border-white/30"
                        >
                          Invite Member
                        </button>
                        <button
                          onClick={() => setShowExportModal(true)}
                          className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg transition-colors border border-white/30"
                        >
                          Export Data
                        </button>
                      </>
                    )}
                    <button
                      onClick={fetchTeamData}
                      className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg transition-colors border border-white/30"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="px-8 py-6">
                  
                  {/* Team Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
                    <div className={`p-6 rounded-lg ${
                      isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                    }`}>
                      <h3 className="text-lg font-semibold mb-2">Total Members</h3>
                      <p className="text-3xl font-bold text-[#100E27]">{teamData.statistics.totalMembers}</p>
                    </div>
                    <div className={`p-6 rounded-lg ${
                      isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                    }`}>
                      <h3 className="text-lg font-semibold mb-2">Active Members</h3>
                      <p className="text-3xl font-bold text-green-600">{teamData.statistics.activeMembers}</p>
                    </div>
                    <div className={`p-6 rounded-lg ${
                      isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                    }`}>
                      <h3 className="text-lg font-semibold mb-2">Inactive Members</h3>
                      <p className="text-3xl font-bold text-yellow-600">{teamData.statistics.inactiveMembers}</p>
                    </div>
                    <div className={`p-6 rounded-lg ${
                      isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                    }`}>
                      <h3 className="text-lg font-semibold mb-2">Departments</h3>
                      <p className="text-3xl font-bold text-blue-600">{teamData.statistics.departments}</p>
                    </div>
                    <div className={`p-6 rounded-lg ${
                      isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                    }`}>
                      <h3 className="text-lg font-semibold mb-2">Roles</h3>
                      <p className="text-3xl font-bold text-purple-600">{teamData.statistics.roles}</p>
                    </div>
                  </div>

 {/* Replace the existing Team Members List section (around line 400-500) with this table format: */}

{/* Team Members Table */}
<div className="space-y-4">
  <div className="flex justify-between items-center">
    <h3 className="text-xl font-semibold">Team Members</h3>
    <div className="text-sm text-gray-500">
      {teamData.teamMembers.length} members
    </div>
  </div>
  
  {teamData.teamMembers.length === 0 ? (
    <div className="text-center py-12">
      <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
      <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        No team members found
      </p>
    </div>
  ) : (
    <div className={`rounded-lg border overflow-hidden ${
      isDarkMode ? 'border-gray-600' : 'border-gray-200'
    }`}>
      {/* Table Header */}
      <div className={`grid grid-cols-12 gap-4 p-4 font-semibold text-sm ${
        isDarkMode ? 'bg-gray-800 border-b border-gray-600' : 'bg-gray-50 border-b border-gray-200'
      }`}>
        <div className="col-span-3">Member</div>
        <div className="col-span-1">Role</div>
        <div className="col-span-1">Department</div>
        <div className="col-span-2">Contact</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2">Last Login</div>
        <div className="col-span-1 text-center">Actions</div>
      </div>

      {/* Table Body */}
      <div className="divide-y divide-gray-200">
        {teamData.teamMembers.map((member, index) => (
          <div 
            key={member.username} 
            className={`grid grid-cols-12 gap-4 p-4 hover:bg-opacity-50 transition-colors ${
              isDarkMode 
                ? `bg-gray-800 hover:bg-gray-700 ${index % 2 === 0 ? 'bg-gray-850' : 'bg-gray-800'}` 
                : `bg-white hover:bg-gray-50 ${index % 2 === 0 ? 'bg-gray-25' : 'bg-white'}`
            } ${member.isCurrentUser ? 'ring-2 ring-[#100E27]/50' : ''}`}
          >
            {/* Member Info */}
            <div className="col-span-3 flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 bg-[#100E27] rounded-full flex items-center justify-center text-white text-sm font-medium overflow-hidden">
                  {member.profileImageUrl ? (
                    <img 
                      src={member.profileImageUrl} 
                      alt={member.fullName}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    getInitials(member)
                  )}
                </div>
                {member.isCurrentUser && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium truncate">{member.fullName}</h4>
                  {member.isCurrentUser && (
                    <span className="px-2 py-0.5 bg-[#100E27] text-white text-xs rounded-full">
                      You
                    </span>
                  )}
                </div>
                <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {member.email}
                </p>
              </div>
            </div>

            {/* Role & Department
            <div className="col-span-2 flex flex-col justify-center">
              <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit mb-1 ${getRoleColor(member.userRole)}`}>
                {member.userRole}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${
                isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}>
                {member.department}
              </span>
            </div> */}

            {/* Role */}
            <div className="col-span-1 flex items-center">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(member.userRole)}`}>
                {member.userRole}
              </span>
            </div>

            {/* Department */}
            <div className="col-span-1 flex items-center">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}>
                {member.department}
              </span>
            </div>


            {/* Contact */}
            <div className="col-span-2 flex flex-col justify-center">
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {member.roleAtAgency}
              </p>
              {member.mobilePhone && (
                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {member.mobilePhone}
                </p>
              )}
            </div>

            {/* Status */}
            <div className="col-span-2 flex flex-col justify-center">
              <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit mb-1 ${getStatusColor(member.status)}`}>
                {member.status}
              </span>
              {/* <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {member.jurisdictionType}
              </span> */}
            </div>

            {/* Last Login Date */}
            {/* console.log("Team member data:", teamData.teamMembers); */}

            <div className="col-span-2 flex flex-col justify-center">
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                Last Login
              </span>
              
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {member.lastLogin ? member.lastLogin : 'Never'}

              </span>
            </div>



            {/* Actions */}
            <div className="col-span-1 flex items-center justify-center gap-1">
              {member.canEdit && teamData.currentUser?.isAdmin && (
                <>
                  <button
                    onClick={() => handleEditMember(member)}
                    className={`p-1.5 text-xs rounded transition-colors ${
                      isDarkMode 
                        ? 'text-blue-400 hover:bg-blue-900/20' 
                        : 'text-blue-600 hover:bg-blue-50'
                    }`}
                    title="Edit Member"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(member)}
                    className={`p-1.5 text-xs rounded transition-colors ${
                      isDarkMode 
                        ? 'text-red-400 hover:bg-red-900/20' 
                        : 'text-red-600 hover:bg-red-50'
                    }`}
                    title="Delete Member"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )}
</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Edit Member Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
            isDarkMode ? 'bg-[#1F2023]' : 'bg-white'
          }`}>
            <div className={`flex justify-between items-center p-6 border-b ${
              isDarkMode ? 'border-gray-600' : 'border-gray-200'
            }`}>
              <h3 className="text-lg font-semibold">Edit Team Member</h3>
              <button
                onClick={() => setEditingMember(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={editingMember.email}
                    onChange={(e) => setEditingMember(prev => ({...prev, email: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">First Name</label>
                  <input
                    type="text"
                    value={editingMember.firstName}
                    onChange={(e) => setEditingMember(prev => ({...prev, firstName: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Last Name</label>
                  <input
                    type="text"
                    value={editingMember.lastName}
                    onChange={(e) => setEditingMember(prev => ({...prev, lastName: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Mobile Phone</label>
                  <input
                    type="tel"
                    value={editingMember.mobilePhone}
                    onChange={(e) => setEditingMember(prev => ({...prev, mobilePhone: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Department</label>
                  <select
                    value={editingMember.department}
                    onChange={(e) => setEditingMember(prev => ({...prev, department: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    {teamData.statistics.departmentsList.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                    <option value="General">General</option>
                    <option value="IT">IT</option>
                    <option value="Development">Development</option>
                    <option value="Business">Business</option>
                    <option value="Operations">Operations</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">User Role</label>
                  <select
                    value={editingMember.userRole}
                    onChange={(e) => setEditingMember(prev => ({...prev, userRole: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="Member">Member</option>
                    <option value="Analyst">Analyst</option>
                    <option value="Developer">Developer</option>
                    <option value="Manager">Manager</option>
                    <option value="Admin">Admin</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">User Status</label>
                  <select
                    value={editingMember.userStatus}
                    onChange={(e) => setEditingMember(prev => ({...prev, userStatus: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Account Status</label>
                  <select
                    value={editingMember.accountStatus}
                    onChange={(e) => setEditingMember(prev => ({...prev, accountStatus: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Jurisdiction Type</label>
                  <select
                    value={editingMember.jurisdictionType}
                    onChange={(e) => setEditingMember(prev => ({...prev, jurisdictionType: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="City">City</option>
                    <option value="County">County</option>
                    <option value="State">State</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Role at Agency</label>
                  <select
                    value={editingMember.roleAtAgency}
                    onChange={(e) => setEditingMember(prev => ({...prev, roleAtAgency: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="IT Administrator">IT Administrator</option>
                    <option value="Accela Developer">Accela Developer</option>
                    <option value="Accela Business Analyst">Accela Business Analyst</option>
                    <option value="Accela Quality Analyst">Accela Quality Analyst</option>
                  </select>
                </div>
              </div>

              {/* Email Updates */}
              <div className="mt-6">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingMember.emailUpdates || false}
                    onChange={(e) => setEditingMember(prev => ({...prev, emailUpdates: e.target.checked}))}
                    className="rounded text-[#100E27] focus:ring-[#100E27]"
                  />
                  <span className="font-medium">Subscribe for email updates</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setEditingMember(null)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 hover:bg-gray-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-[#100E27] text-white rounded-lg hover:bg-[#1a1a2e] transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-full max-w-md mx-4 rounded-lg shadow-xl ${
            isDarkMode ? 'bg-[#1F2023]' : 'bg-white'
          }`}>
            <div className={`flex justify-between items-center p-6 border-b ${
              isDarkMode ? 'border-gray-600' : 'border-gray-200'
            }`}>
              <h3 className="text-lg font-semibold">Confirm Delete</h3>
              <button
                onClick={() => setShowDeleteModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium mb-2">Delete Team Member</h3>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Are you sure you want to delete <strong>{showDeleteModal.fullName}</strong>? 
                  This will set their account status to "deleted" and they will no longer be able to sign in.
                </p>
                <p className={`text-xs mb-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  This action can be reversed by creating a new account for this user.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 hover:bg-gray-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteMember(showDeleteModal.username)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Member
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Data Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-full max-w-md mx-4 rounded-lg shadow-xl ${
            isDarkMode ? 'bg-[#1F2023]' : 'bg-white'
          }`}>
            <div className={`flex justify-between items-center p-6 border-b ${
              isDarkMode ? 'border-gray-600' : 'border-gray-200'
            }`}>
              <h3 className="text-lg font-semibold">Export Team Data</h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Export Format</label>
                  <select
                    value={exportOptions.format}
                    onChange={(e) => setExportOptions(prev => ({...prev, format: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="excel">Excel (.xlsx)</option>
                    <option value="csv">CSV (.csv)</option>
                    <option value="pdf">PDF (.pdf)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Filter by Status</label>
                  <select
                    value={exportOptions.filters.status}
                    onChange={(e) => setExportOptions(prev => ({
                      ...prev, 
                      filters: {...prev.filters, status: e.target.value}
                    }))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active Only</option>
                    <option value="inactive">Inactive Only</option>
                    <option value="deleted">Deleted Only</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Filter by Department</label>
                  <select
                    value={exportOptions.filters.department}
                    onChange={(e) => setExportOptions(prev => ({
                      ...prev, 
                      filters: {...prev.filters, department: e.target.value}
                    }))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="">All Departments</option>
                    {teamData.statistics.departmentsList.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Filter by Role</label>
                  <select
                    value={exportOptions.filters.userRole}
                    onChange={(e) => setExportOptions(prev => ({
                      ...prev, 
                      filters: {...prev.filters, userRole: e.target.value}
                    }))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="">All Roles</option>
                    {teamData.statistics.rolesList.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowExportModal(false)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 hover:bg-gray-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  className="px-4 py-2 bg-[#100E27] text-white rounded-lg hover:bg-[#1a1a2e] transition-colors"
                >
                  Export Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Member Modal - Same as before */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-full max-w-md mx-4 rounded-lg shadow-xl ${
            isDarkMode ? 'bg-[#1F2023]' : 'bg-white'
          }`}>
            <div className={`flex justify-between items-center p-6 border-b ${
              isDarkMode ? 'border-gray-600' : 'border-gray-200'
            }`}>
              <h3 className="text-lg font-semibold">Invite Team Member</h3>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteForm({ email: '', role: 'Member', department: 'General' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>

            <form onSubmit={handleInvite} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email Address</label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm(prev => ({...prev, email: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                    required
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Role</label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm(prev => ({...prev, role: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="Member">Member</option>
                    <option value="Analyst">Analyst</option>
                    <option value="Developer">Developer</option>
                    <option value="Manager">Manager</option>
                    <option value="Viewer">Viewer</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Department</label>
                  <select
                    value={inviteForm.department}
                    onChange={(e) => setInviteForm(prev => ({...prev, department: e.target.value}))}
                    className={`w-full p-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-[#100E27]/50 focus:border-[#100E27]`}
                  >
                    <option value="General">General</option>
                    <option value="IT">IT</option>
                    <option value="Development">Development</option>
                    <option value="Business">Business</option>
                    <option value="Operations">Operations</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteForm({ email: '', role: 'Member', department: 'General' });
                  }}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 hover:bg-gray-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#100E27] text-white rounded-lg hover:bg-[#1a1a2e] transition-colors"
                >
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;